import { offlineModeManager } from "./offlineModeManager";

// Constants
const MAX_BLOB_SIZE = 100 * 1024 * 1024; // 100 MB
const CLEANUP_MAX_AGE_DAYS = 7; // 7 days
const MAX_ERROR_HISTORY = 10; // Keep last 10 errors

class IndexedDBUploadManager {
  constructor() {
    this.dbName = "AssessmentUploads";
    this.storeName = "pendingUploads";
    this.db = null;
    this.initPromise = null;
    this.isAvailable = false;
  }

  async init() {
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      if (!offlineModeManager.isOfflineModeSupported()) {
        const error = new Error(
          offlineModeManager.getErrorMessage() || "IndexedDB not available"
        );
        this.isAvailable = false;
        reject(error);
        return;
      }

      const request = indexedDB.open(this.dbName, 1);

      request.onerror = () => {
        this.isAvailable = false;
        reject(request.error || new Error("Failed to open IndexedDB"));
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.isAvailable = true;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, {
            keyPath: "questionId",
          });

          store.createIndex("status", "status", { unique: false });
          store.createIndex("timestamp", "timestamp", { unique: false });
          store.createIndex("attempts", "attempts", { unique: false });
        }
      };
    });

    return this.initPromise;
  }

  // Helper method to wrap IDBRequest in Promise
  wrapIDBRequest(request) {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async verifyDatabaseHealth() {
    try {
      if (!this.isAvailable) {
        return false;
      }

      await this.init();

      const transaction = this.db.transaction([this.storeName], "readonly");
      const store = transaction.objectStore(this.storeName);

      await this.wrapIDBRequest(store.count());

      return true;
    } catch (error) {
      console.error("Database health check failed:", error);
      return false;
    }
  }

  // ✅ TEST FUNCTION: Store test data to verify IndexedDB works
  async storeTestData() {
    console.log("🧪 [IndexedDB Manager] Storing test data...");

    if (!offlineModeManager.isOfflineModeSupported()) {
      console.error(
        "❌ [IndexedDB Manager] Cannot store test data - offline mode not supported"
      );
      return false;
    }

    await this.init();

    const testQuestionId = "TEST_DATA_" + Date.now();
    const testBlob = new Blob(["TEST DATA"], { type: "text/plain" });
    const testArrayBuffer = await testBlob.arrayBuffer();

    const testRecord = {
      questionId: testQuestionId,
      fileBlob: testArrayBuffer,
      fileType: "text/plain",
      fileName: "test.txt",
      formFields: { test: "true" },
      uploadData: null,
      copyPasteAnalysis: null,
      cheatingCounts: { fullScreenExitCount: 0, tabSwitchCount: 0 },
      status: "pending",
      attempts: 0,
      maxAttempts: 3,
      timestamp: Date.now(),
      lastAttemptTime: null,
      error: null,
    };

    try {
      const transaction = this.db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);

      // Add transaction completion handlers
      transaction.oncomplete = () => {
        console.log(
          "✅ [IndexedDB Manager] Test data transaction COMPLETED successfully"
        );
      };

      transaction.onerror = () => {
        console.error(
          "❌ [IndexedDB Manager] Test data transaction ERROR:",
          transaction.error
        );
      };

      transaction.onabort = () => {
        console.error("❌ [IndexedDB Manager] Test data transaction ABORTED");
      };

      const request = store.put(testRecord);

      await new Promise((resolve, reject) => {
        request.onsuccess = () => {
          console.log("✅ [IndexedDB Manager] Test data PUT request succeeded");
          resolve(request.result);
        };

        request.onerror = () => {
          console.error(
            "❌ [IndexedDB Manager] Test data PUT request failed:",
            request.error
          );
          reject(request.error);
        };
      });

      // ✅ VERIFY: Immediately read back the data
      await new Promise((resolve) => {
        const verifyTransaction = this.db.transaction(
          [this.storeName],
          "readonly"
        );
        const verifyStore = verifyTransaction.objectStore(this.storeName);
        const verifyRequest = verifyStore.get(testQuestionId);

        verifyRequest.onsuccess = () => {
          if (verifyRequest.result) {
            console.log(
              "✅ [IndexedDB Manager] Test data VERIFIED - can be read back:",
              {
                questionId: verifyRequest.result.questionId,
                blobSize: verifyRequest.result.fileBlob?.byteLength,
                status: verifyRequest.result.status,
              }
            );
          } else {
            console.error(
              "❌ [IndexedDB Manager] Test data NOT FOUND after storage!"
            );
          }
          resolve();
        };

        verifyRequest.onerror = () => {
          console.error(
            "❌ [IndexedDB Manager] Failed to verify test data:",
            verifyRequest.error
          );
          resolve();
        };
      });

      return true;
    } catch (error) {
      console.error("❌ [IndexedDB Manager] Error storing test data:", error);
      return false;
    }
  }

  // Store complete upload data including blob
  async storePendingUpload(questionId, uploadData) {
    console.log(
      "📦 [IndexedDB Manager] storePendingUpload called for question:",
      questionId
    );

    if (!offlineModeManager.isOfflineModeSupported()) {
      console.error(
        "❌ [IndexedDB Manager] Offline mode not supported - cannot store data"
      );
      throw new Error("IndexedDB unavailable - cannot store offline data");
    }

    await this.init();

    // ✅ CRITICAL FIX: Convert blob to ArrayBuffer BEFORE creating transaction
    // This prevents the transaction from auto-committing during the async operation
    const fileBlob = uploadData.formData.get("file");
    if (!fileBlob) {
      throw new Error("No file blob found in formData");
    }

    // Convert blob to ArrayBuffer BEFORE transaction
    const arrayBuffer = await fileBlob.arrayBuffer();

    // ✅ Add size validation before storing
    const blobSizeMB = (arrayBuffer.byteLength / 1024 / 1024).toFixed(2);

    if (arrayBuffer.byteLength > MAX_BLOB_SIZE) {
      const error = new Error(
        `Blob too large: ${blobSizeMB} MB. Maximum allowed: 100 MB`
      );
      console.error("❌ [IndexedDB Manager] Blob size exceeds limit:", {
        questionId,
        blobSize: arrayBuffer.byteLength,
        blobSizeMB,
        maxSizeMB: (MAX_BLOB_SIZE / 1024 / 1024).toFixed(2),
      });
      throw error;
    }

    console.log("📊 [IndexedDB Manager] Blob size:", {
      bytes: arrayBuffer.byteLength,
      mb: blobSizeMB,
      questionId,
    });

    // Extract all form fields BEFORE transaction
    // ✅ IMPORTANT: Clone FormData entries to avoid any potential issues
    const formFields = {};
    try {
      for (const [key, value] of uploadData.formData.entries()) {
        if (key !== "file") {
          // Store primitive values directly, convert others to strings
          formFields[key] = typeof value === "string" ? value : String(value);
        }
      }

      // ✅ NEW: Log what fields were extracted for debugging
      console.log(
        `📋 [IndexedDB Manager] Extracted formFields for question ${questionId}:`,
        Object.keys(formFields)
      );
    } catch (error) {
      console.error(
        `❌ [IndexedDB Manager] Error extracting form fields for question ${questionId}:`,
        error
      );
      // Continue with empty formFields if extraction fails
    }

    // Extract question metadata from formData
    const questionTitle =
      formFields.questionTitle || uploadData.questionTitle || null;
    const questionType =
      formFields.type ||
      uploadData.questionType ||
      (formFields.fileType?.includes("video") ? "video" : "audio");

    // Prepare upload record BEFORE transaction
    const uploadRecord = {
      questionId,
      // Store blob as ArrayBuffer
      fileBlob: arrayBuffer,
      fileType: fileBlob.type || "audio/webm",
      fileName:
        fileBlob.name ||
        (fileBlob.type && fileBlob.type.includes("video")
          ? "recordedVideo.webm"
          : "recordedAudio.webm"),
      // Store all form fields
      formFields,
      // Store upload metadata
      uploadData: uploadData.uploadData,
      copyPasteAnalysis: uploadData.copyPasteAnalysis,
      cheatingCounts: uploadData.cheatingCounts,
      // Store question metadata for processing page
      questionTitle,
      questionType,
      // Status tracking
      status: "pending", // 'pending' | 'uploading' | 'failed' | 'completed'
      attempts: 0,
      maxAttempts: 3,
      timestamp: Date.now(),
      lastAttemptTime: null,
      error: null,
      // ✅ NEW: Separate status tracking for upload and analysis
      azureUploadStatus: "pending", // 'pending' | 'uploading' | 'completed' | 'failed'
      analysisStatus: "pending", // 'pending' | 'completed' | 'failed'
      analysisPayload: null, // Store analysis payload when upload succeeds
      azureUploadCompletedAt: null, // Timestamp when Azure upload completed
      analysisCompletedAt: null, // Timestamp when analysis completed
    };

    // ✅ ULTIMATE FIX: Create transaction, request, and attach handlers ALL synchronously
    // BEFORE returning ANY promise. This prevents ANY microtask gaps that could cause
    // transaction auto-commit, especially under heavy thread load.

    // Step 1: Create transaction synchronously
    const transaction = this.db.transaction([this.storeName], "readwrite");
    const store = transaction.objectStore(this.storeName);

    // Step 2: Queue request IMMEDIATELY and synchronously
    const request = store.put(uploadRecord);

    // Step 3: Create promise holder synchronously
    let resolvePromise;
    let rejectPromise;
    const resultPromise = new Promise((resolve, reject) => {
      resolvePromise = resolve;
      rejectPromise = reject;
    });

    // ✅ CRITICAL FIX: Track request and transaction completion separately
    let requestCompleted = false;
    let transactionCompleted = false;
    let storedResult = null;
    let transactionError = null;

    // Step 4: Attach request handlers IMMEDIATELY and synchronously
    request.onsuccess = () => {
      console.log("✅ [IndexedDB Manager] PUT request succeeded:", {
        questionId,
        blobSize: arrayBuffer.byteLength,
        fileType: uploadRecord.fileType,
        status: uploadRecord.status,
      });
      requestCompleted = true;
      storedResult = request.result;

      // Don't resolve yet - wait for transaction completion
    };

    request.onerror = () => {
      const error = request.error || new Error("Put operation failed");

      // ✅ Check for quota errors
      if (error.name === "QuotaExceededError") {
        console.error("❌ [IndexedDB Manager] Storage quota exceeded:", {
          questionId,
          blobSize: arrayBuffer.byteLength,
          blobSizeMB: (arrayBuffer.byteLength / 1024 / 1024).toFixed(2),
        });
        rejectPromise(
          new Error(
            `Storage quota exceeded. Blob size: ${(
              arrayBuffer.byteLength /
              1024 /
              1024
            ).toFixed(2)} MB`
          )
        );
        return;
      }

      // ✅ Check for data clone errors (non-serializable data)
      if (error.name === "DataCloneError") {
        console.error(
          "❌ [IndexedDB Manager] Data clone error - non-serializable data:",
          {
            questionId,
            blobSize: arrayBuffer.byteLength,
          }
        );
        rejectPromise(
          new Error("Data cannot be stored - contains non-serializable objects")
        );
        return;
      }

      console.error("❌ [IndexedDB Manager] Put request error:", error, {
        questionId,
        blobSize: arrayBuffer.byteLength,
        fileType: uploadRecord.fileType,
        errorName: error.name,
        errorMessage: error.message,
      });
      rejectPromise(error);
    };

    // ✅ CRITICAL FIX: Wait for transaction completion before resolving
    transaction.oncomplete = () => {
      console.log(
        "✅ [IndexedDB Manager] Transaction COMPLETED - data is persisted:",
        {
          questionId,
          blobSize: arrayBuffer.byteLength,
        }
      );
      transactionCompleted = true;

      // Only proceed if request also succeeded
      if (requestCompleted && !transactionError) {
        // ✅ VERIFY: Read back immediately to confirm data exists
        const verifyTransaction = this.db.transaction(
          [this.storeName],
          "readonly"
        );
        const verifyStore = verifyTransaction.objectStore(this.storeName);
        const verifyRequest = verifyStore.get(questionId);

        verifyRequest.onsuccess = () => {
          if (verifyRequest.result) {
            console.log(
              "✅ [IndexedDB Manager] VERIFICATION SUCCESS - Data confirmed in IndexedDB:",
              {
                questionId: verifyRequest.result.questionId,
                blobSize: verifyRequest.result.fileBlob?.byteLength,
                status: verifyRequest.result.status,
              }
            );
            resolvePromise(storedResult);
          } else {
            console.error(
              "❌ [IndexedDB Manager] VERIFICATION FAILED - Data NOT found after transaction completion!"
            );
            rejectPromise(
              new Error(
                "Data verification failed - record not found in IndexedDB after transaction completion"
              )
            );
          }
        };

        verifyRequest.onerror = () => {
          console.error(
            "❌ [IndexedDB Manager] Verification read failed:",
            verifyRequest.error
          );
          // Transaction completed but verification failed - still reject to be safe
          rejectPromise(
            new Error(
              `Verification failed: ${
                verifyRequest.error?.message || "Unknown error"
              }`
            )
          );
        };
      } else if (!requestCompleted) {
        // Transaction completed but request didn't succeed - this shouldn't happen
        console.error(
          "❌ [IndexedDB Manager] Transaction completed but request didn't succeed!"
        );
        rejectPromise(
          new Error("Transaction completed but PUT request failed")
        );
      }
    };

    transaction.onerror = () => {
      transactionError = transaction.error || new Error("Transaction failed");
      console.error(
        "❌ [IndexedDB Manager] Transaction ERROR:",
        transactionError,
        {
          questionId,
          blobSize: arrayBuffer.byteLength,
          errorName: transactionError.name,
          errorMessage: transactionError.message,
        }
      );
      rejectPromise(transactionError);
    };

    transaction.onabort = () => {
      transactionError = new Error("Transaction was aborted");
      console.error("❌ [IndexedDB Manager] Transaction ABORTED:", {
        questionId,
        blobSize: arrayBuffer.byteLength,
      });
      rejectPromise(transactionError);
    };

    // Step 5: Return the promise (request is already queued with handlers attached)
    return resultPromise;
  }

  // Restore upload data from IndexedDB
  async getPendingUpload(questionId) {
    await this.init();

    const transaction = this.db.transaction([this.storeName], "readonly");
    const store = transaction.objectStore(this.storeName);

    // ✅ FIX: Wrap IDBRequest in promise
    const record = await this.wrapIDBRequest(store.get(questionId));

    if (!record) return null;

    // ✅ CRITICAL FIX: Skip records already marked as completed to avoid duplicate processing
    if (record.status === "completed") {
      console.log(
        `⏭️ [IndexedDB Manager] Skipping getPendingUpload for question ${questionId} - status is completed`
      );
      return null;
    }

    // Reconstruct blob from ArrayBuffer
    const blob = new Blob([record.fileBlob], { type: record.fileType });

    // Reconstruct FormData
    const formData = new FormData();
    formData.append("file", blob, record.fileName);

    // Add all form fields (handle cases where formFields might be null/undefined)
    Object.entries(record.formFields || {}).forEach(([key, value]) => {
      formData.append(key, value);
    });

    // ✅ CRITICAL FIX: Ensure "type" exists in reconstructed formData
    const hasValidType =
      formData.has("type") &&
      formData.get("type") !== null &&
      formData.get("type") !== "null" &&
      formData.get("type") !== "undefined" &&
      formData.get("type") !== "";

    if (!hasValidType) {
      const fallbackType =
        record.questionType ||
        (record.fileType?.includes("video")
          ? "video"
          : record.fileType?.includes("audio")
            ? "audio"
            : null);

      if (fallbackType) {
        formData.set("type", fallbackType);
        console.log(
          `✅ [IndexedDB Manager] Restored missing "type" field using questionType (${fallbackType}) for question ${questionId}`
        );
      }
    }

    return {
      formData,
      uploadData: record.uploadData,
      copyPasteAnalysis: record.copyPasteAnalysis,
      cheatingCounts: record.cheatingCounts,
      status: record.status,
      attempts: record.attempts,
      maxAttempts: record.maxAttempts,
      timestamp: record.timestamp,
      error: record.error,
      questionType: record.questionType,
      formFields: record.formFields || {},
      // ✅ NEW: Return separate status fields (with backward compatibility)
      azureUploadStatus:
        record.azureUploadStatus ||
        (record.status === "completed" ? "completed" : "pending"),
      analysisStatus: record.analysisStatus || "pending",
      analysisPayload: record.analysisPayload || null,
      azureUploadCompletedAt: record.azureUploadCompletedAt || null,
      analysisCompletedAt: record.analysisCompletedAt || null,
    };
  }

  // Get all pending uploads (returns raw records for status checking)
  async getAllPendingUploads() {
    await this.init();

    const transaction = this.db.transaction([this.storeName], "readonly");
    const store = transaction.objectStore(this.storeName);
    const index = store.index("status");

    // ✅ FIX: Wrap IDBRequest in promises
    const getRecordsByStatus = (status) => {
      return this.wrapIDBRequest(index.getAll(status)).then(
        (result) => result || []
      );
    };

    try {
      // Get all records with status 'pending' or 'failed'
      const pending = await getRecordsByStatus("pending");
      const failed = await getRecordsByStatus("failed");

      // ✅ Filter by questionType - only return audio/video uploads
      const fileUploads = [...pending, ...failed].filter((record) => {
        const questionType = record.questionType;
        // Only return audio and video uploads
        if (questionType !== "audio" && questionType !== "video") {
          return false;
        }

        // ✅ NEW: Skip if already uploaded (azureUploadStatus === "completed")
        // This prevents retrying uploads that are already in Azure
        if (record.azureUploadStatus === "completed") {
          console.log(
            `⏭️ [IndexedDB Manager] Skipping question ${record.questionId} - already uploaded to Azure`
          );
          return false;
        }

        return true;
      });

      // ✅ NEW: Normalize questionId (remove ANSWER_ prefix if present for uploads)
      // Uploads should not have ANSWER_ prefix, only text answers should
      const normalizedUploads = fileUploads.map((record) => {
        let questionId = record.questionId;
        // Remove ANSWER_ prefix if present (shouldn't be there for uploads, but handle it)
        if (questionId.startsWith("ANSWER_")) {
          questionId = questionId.replace("ANSWER_", "");
          console.warn(
            `⚠️ [IndexedDB Manager] Found ANSWER_ prefix on upload record for question ${questionId} - normalizing`
          );
        }
        return {
          questionId,
          questionType: record.questionType,
          status: record.status,
          attempts: record.attempts,
          timestamp: record.timestamp,
          error: record.error,
        };
      });

      // ✅ NEW: Deduplicate by questionId (in case there are duplicates)
      const uniqueUploads = new Map();
      for (const upload of normalizedUploads) {
        if (!uniqueUploads.has(upload.questionId)) {
          uniqueUploads.set(upload.questionId, upload);
        } else {
          console.warn(
            `⚠️ [IndexedDB Manager] Duplicate upload record found for question ${upload.questionId} - keeping first one`
          );
        }
      }

      return Array.from(uniqueUploads.values());
    } catch (error) {
      console.error("Error getting pending uploads:", error);
      return []; // Return empty array on error
    }
  }

  // Get count of pending uploads
  async getPendingCount() {
    const pending = await this.getAllPendingUploads();
    return pending.length;
  }

  // Update upload status
  // ✅ ENHANCED: Accept optional metadata parameter for separate status tracking
  async updateUploadStatus(questionId, status, error = null, metadata = {}) {
    await this.init();

    // ✅ CRITICAL FIX: Create transaction and perform both get and put in same scope
    // This prevents transaction from auto-committing between operations
    const transaction = this.db.transaction([this.storeName], "readwrite");
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      // Handle transaction errors
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(new Error("Transaction was aborted"));

      // First, get the record
      const getRequest = store.get(questionId);
      getRequest.onsuccess = () => {
        const record = getRequest.result;
        if (!record) {
          resolve(null);
          return;
        }

        // ✅ ENHANCED: Update record fields with error history tracking
        const previousStatus = record.status;
        record.status = status;
        record.lastAttemptTime = Date.now();

        // ✅ NEW: Initialize separate status fields if not present (backward compatibility)
        if (record.azureUploadStatus === undefined) {
          record.azureUploadStatus =
            record.status === "completed" ? "completed" : "pending";
        }
        if (record.analysisStatus === undefined) {
          record.analysisStatus = "pending";
        }

        // ✅ NEW: Update separate status fields from metadata
        if (metadata.azureUploadStatus !== undefined) {
          record.azureUploadStatus = metadata.azureUploadStatus;
          if (metadata.azureUploadStatus === "completed") {
            record.azureUploadCompletedAt = Date.now();
          }
        }
        if (metadata.analysisStatus !== undefined) {
          record.analysisStatus = metadata.analysisStatus;
          if (metadata.analysisStatus === "completed") {
            record.analysisCompletedAt = Date.now();
          }
        }
        if (metadata.analysisPayload !== undefined) {
          record.analysisPayload = metadata.analysisPayload;
        }

        // Only increment attempts if status changed to retrying or failed
        if (
          status === "retrying" ||
          (status === "failed" && previousStatus !== "failed")
        ) {
          record.attempts = (record.attempts || 0) + 1;
        }

        // ✅ ENHANCED: Track error history
        if (error) {
          record.error = error;
          if (!record.errorHistory) {
            record.errorHistory = [];
          }
          record.errorHistory.push({
            error,
            status,
            timestamp: Date.now(),
            attempt: record.attempts,
          });
          // Keep only last N errors
          if (record.errorHistory.length > MAX_ERROR_HISTORY) {
            record.errorHistory = record.errorHistory.slice(-MAX_ERROR_HISTORY);
          }
        }

        // Immediately put the updated record (transaction is still active)
        const putRequest = store.put(record);
        putRequest.onsuccess = () => resolve(putRequest.result);
        putRequest.onerror = () => reject(putRequest.error);
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  // ✅ NEW: Get upload status with separate azureUploadStatus and analysisStatus
  async getUploadStatus(questionId) {
    await this.init();

    const transaction = this.db.transaction([this.storeName], "readonly");
    const store = transaction.objectStore(this.storeName);

    const record = await this.wrapIDBRequest(store.get(questionId));

    if (!record) {
      return null;
    }

    // ✅ Backward compatibility: Initialize fields if not present
    const azureUploadStatus =
      record.azureUploadStatus ||
      (record.status === "completed" ? "completed" : "pending");
    const analysisStatus = record.analysisStatus || "pending";

    return {
      questionId: record.questionId,
      status: record.status,
      azureUploadStatus,
      analysisStatus,
      analysisPayload: record.analysisPayload || null,
      azureUploadCompletedAt: record.azureUploadCompletedAt || null,
      analysisCompletedAt: record.analysisCompletedAt || null,
      uploadData: record.uploadData || null,
      attempts: record.attempts || 0,
      maxAttempts: record.maxAttempts || 3,
      error: record.error || null,
      timestamp: record.timestamp || null,
    };
  }

  // Mark upload as completed and remove
  async markUploadCompleted(questionId) {
    await this.init();
    console.log(
      "✅ [IndexedDB Manager] Marking upload as completed for question:",
      questionId
    );
    const transaction = this.db.transaction([this.storeName], "readwrite");
    const store = transaction.objectStore(this.storeName);

    // ✅ FIX: Wrap IDBRequest in promise to ensure deletion completes
    return new Promise((resolve, reject) => {
      const request = store.delete(questionId);

      request.onsuccess = () => {
        console.log(
          `✅ [IndexedDB Manager] Delete request succeeded for question: ${questionId}`
        );
        // Wait for transaction to complete before resolving
      };

      request.onerror = () => {
        const error = request.error || new Error("Delete operation failed");
        console.error(
          `❌ [IndexedDB Manager] Delete request error for question ${questionId}:`,
          error
        );
        reject(error);
      };

      // ✅ CRITICAL: Wait for transaction completion to ensure deletion is persisted
      transaction.oncomplete = () => {
        resolve();
      };

      transaction.onerror = () => {
        const error = transaction.error || new Error("Transaction failed");
        console.error(
          `❌ [IndexedDB Manager] Transaction error for question ${questionId}:`,
          error
        );
        reject(error);
      };
    });
  }

  // Process all pending uploads (for offline queue)
  async processPendingUploads(onProgress = null) {
    const pending = await this.getAllPendingUploads();

    for (const upload of pending) {
      try {
        // Mark as uploading
        await this.updateUploadStatus(upload.questionId, "uploading");

        // Restore upload data
        const uploadData = await this.getPendingUpload(upload.questionId);

        if (onProgress) {
          onProgress(upload.questionId, "uploading", upload);
        }

        // Attempt upload
        const { completeBackgroundUpload } =
          await import("../../services/assessment.api");
        await completeBackgroundUpload(
          uploadData.formData,
          uploadData.uploadData,
          uploadData.copyPasteAnalysis,
          uploadData.cheatingCounts,
          null
        );

        // Mark as completed
        await this.markUploadCompleted(upload.questionId);

        if (onProgress) {
          onProgress(upload.questionId, "completed", upload);
        }
      } catch (error) {
        // Check if max attempts reached
        if (upload.attempts >= upload.maxAttempts) {
          await this.updateUploadStatus(
            upload.questionId,
            "failed",
            error.message
          );
        } else {
          await this.updateUploadStatus(
            upload.questionId,
            "pending",
            error.message
          );
        }

        if (onProgress) {
          onProgress(upload.questionId, "failed", upload, error);
        }
      }
    }
  }

  // Clear all completed/failed uploads older than X days
  async cleanupOldUploads(maxAgeDays = CLEANUP_MAX_AGE_DAYS) {
    await this.init();

    const transaction = this.db.transaction([this.storeName], "readwrite");
    const store = transaction.objectStore(this.storeName);
    const index = store.index("timestamp");

    const cutoffTime = Date.now() - maxAgeDays * 24 * 60 * 60 * 1000;
    const range = IDBKeyRange.upperBound(cutoffTime);

    return new Promise((resolve, reject) => {
      const request = index.openCursor(range);

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const record = cursor.value;
          if (record.status === "completed" || record.status === "failed") {
            cursor.delete();
          }
          cursor.continue();
        } else {
          resolve();
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // ✅ ENHANCED: Store text-based answer (MCQ, Subjective, Programming) with complete payload
  async storePendingAnswer(questionId, answerData) {
    if (!offlineModeManager.isOfflineModeSupported()) {
      throw new Error("IndexedDB unavailable - cannot store offline data");
    }

    await this.init();

    const transaction = this.db.transaction([this.storeName], "readwrite");
    const store = transaction.objectStore(this.storeName);

    // ✅ Use composite key to avoid conflicts with file uploads
    const compositeKey = `ANSWER_${questionId}`;

    // ✅ ENHANCED: Store complete payload with all required fields
    const answerRecord = {
      questionId: compositeKey,
      originalQuestionId: questionId, // Store original for retrieval

      // ✅ Complete answer payload (ready for retry)
      answerPayload: answerData.payload || answerData, // Support both formats

      // Question type
      questionType:
        answerData.questionType || answerData.payload?.type || "unknown",

      // Question title (extract from payload if available)
      questionTitle:
        answerData.questionTitle || answerData.payload?.questionTitle || null,

      // API call metadata
      apiUrl:
        answerData.apiUrl || "/candidateAssessment/update-candidate-result",
      apiMethod: answerData.apiMethod || "PATCH",
      candidateAssessmentId:
        answerData.candidateAssessmentId ||
        answerData.payload?.candidateAssessmentId,

      // ✅ ENHANCED: Store all analysis data separately for easier access
      typingAnalysis:
        answerData.typingAnalysis || answerData.payload?.typingAnalysis || null,
      copyPasteAnalysis:
        answerData.copyPasteAnalysis ||
        answerData.payload?.copyPasteAnalysis ||
        null,
      cheatingCounts: answerData.cheatingCounts || {
        fullScreenExitCount: answerData.payload?.fullScreenExitCount || 0,
        tabSwitchCount: answerData.payload?.tabSwitchCount || 0,
      },

      // ✅ ENHANCED: Retry tracking
      status: "pending",
      attempts: answerData.attempts || 0,
      maxAttempts: answerData.maxAttempts || 3,
      timestamp: Date.now(),
      lastAttemptTime: answerData.lastAttemptTime || null,
      error: answerData.error || null,
      errorHistory: answerData.errorHistory || [], // Track all errors

      // Flag to distinguish from file uploads
      isTextAnswer: true,
    };

    // ✅ FIX: Wrap IDBRequest in promise to ensure transaction stays active
    return new Promise((resolve, reject) => {
      const request = store.put(answerRecord);
      request.onsuccess = () => {
        console.log(
          `✅ [IndexedDB Manager] Stored text answer for question ${questionId}`
        );
        resolve(request.result);
      };
      request.onerror = () => {
        console.error(
          `❌ [IndexedDB Manager] Error storing text answer for question ${questionId}:`,
          request.error
        );
        reject(request.error);
      };
    });
  }

  // ✅ ENHANCED: Get pending text answer with complete data
  async getPendingAnswer(questionId) {
    await this.init();

    const transaction = this.db.transaction([this.storeName], "readonly");
    const store = transaction.objectStore(this.storeName);
    const compositeKey = `ANSWER_${questionId}`;

    // ✅ FIX: Wrap IDBRequest in promise
    const record = await this.wrapIDBRequest(store.get(compositeKey));

    if (!record || !record.isTextAnswer) return null;

    // ✅ ENHANCED: Return complete data structure for retry
    return {
      questionId: record.originalQuestionId,
      payload: record.answerPayload, // Complete payload ready for API call
      questionType: record.questionType,
      apiUrl: record.apiUrl,
      apiMethod: record.apiMethod,
      candidateAssessmentId: record.candidateAssessmentId,
      typingAnalysis: record.typingAnalysis,
      copyPasteAnalysis: record.copyPasteAnalysis,
      cheatingCounts: record.cheatingCounts,
      status: record.status,
      attempts: record.attempts || 0,
      maxAttempts: record.maxAttempts || 3,
      timestamp: record.timestamp,
      lastAttemptTime: record.lastAttemptTime,
      error: record.error,
      errorHistory: record.errorHistory || [],
    };
  }

  // ✅ NEW: Get all pending text answers
  async getAllPendingAnswers() {
    await this.init();

    const transaction = this.db.transaction([this.storeName], "readonly");
    const store = transaction.objectStore(this.storeName);

    // ✅ FIX: Wrap IDBRequest in promise
    const allRecords = await this.wrapIDBRequest(store.getAll()).then(
      (result) => result || []
    );

    return allRecords
      .filter((r) => {
        const questionType = r.questionType;
        // ✅ Filter by questionType only - return all non-audio/video records
        // This includes ANSWER, DRAFT, and EVENTS records
        // ✅ FIX: Include "retrying" status to resume interrupted retries
        return (
          questionType &&
          questionType !== "audio" &&
          questionType !== "video" &&
          (r.status === "pending" ||
            r.status === "failed" ||
            r.status === "retrying")
        );
      })
      .map((record) => {
        // ✅ FIX: Ensure originalQuestionId exists for text answers
        // If originalQuestionId is missing, try to extract from composite key
        let questionId = record.originalQuestionId;
        if (!questionId && record.isTextAnswer) {
          // Extract from ANSWER_ prefix if present
          questionId =
            record.questionId?.replace(/^ANSWER_/, "") || record.questionId;
        } else if (!questionId) {
          questionId = record.questionId;
        }

        return {
          questionId,
          questionType: record.questionType,
          status: record.status,
          attempts: record.attempts,
          timestamp: record.timestamp,
          error: record.error,
          // ✅ Include record type for handling
          isDraft: record.isDraft || false,
          isEditorEvents: record.isEditorEvents || false,
          isTextAnswer: record.isTextAnswer || false,
          // ✅ Include languageId for DRAFT/EVENTS records
          languageId: record.languageId || null,
        };
      });
  }

  // ✅ NEW: Process all pending text answers
  async processPendingAnswers(updateCandidateResultFn) {
    await this.init();

    const pendingAnswers = await this.getAllPendingAnswers();
    let processedCount = 0;

    for (const answer of pendingAnswers) {
      try {
        const compositeKey = `ANSWER_${answer.questionId}`;
        await this.updateUploadStatus(compositeKey, "uploading");

        const answerData = await this.getPendingAnswer(answer.questionId);
        if (!answerData) continue;

        // Submit answer using the stored API call
        await updateCandidateResultFn(
          answerData.candidateAssessmentId,
          answerData.payload
        );

        // Mark as completed
        await this.markUploadCompleted(compositeKey);
        processedCount++;
      } catch (error) {
        const compositeKey = `ANSWER_${answer.questionId}`;
        const answerData = await this.getPendingAnswer(answer.questionId);
        if (answerData) {
          if (answerData.attempts >= answerData.maxAttempts) {
            await this.updateUploadStatus(
              compositeKey,
              "failed",
              error.message
            );
          } else {
            await this.updateUploadStatus(
              compositeKey,
              "pending",
              error.message
            );
          }
        }
      }
    }

    return processedCount;
  }

  // ✅ NEW: Store final test submission
  async storePendingSubmission(candidateAssessmentId, submissionData) {
    if (!offlineModeManager.isOfflineModeSupported()) {
      throw new Error("IndexedDB unavailable - cannot store offline data");
    }

    await this.init();

    const transaction = this.db.transaction([this.storeName], "readwrite");
    const store = transaction.objectStore(this.storeName);

    // ✅ Unique key for submission
    const submissionKey = `SUBMISSION_${candidateAssessmentId}`;

    const submissionRecord = {
      questionId: submissionKey,
      submissionPayload: submissionData.payload,
      assessmentId: submissionData.assessmentId,
      candidateAssessmentId,
      // Status tracking
      status: "pending",
      attempts: 0,
      maxAttempts: 3,
      timestamp: Date.now(),
      lastAttemptTime: null,
      error: null,
      // Flag to distinguish from answers
      isFinalSubmission: true,
    };

    // ✅ FIX: Wrap IDBRequest in promise to ensure transaction stays active
    return this.wrapIDBRequest(store.put(submissionRecord));
  }

  // ✅ NEW: Get pending final submission
  async getPendingSubmission(candidateAssessmentId) {
    await this.init();

    const transaction = this.db.transaction([this.storeName], "readonly");
    const store = transaction.objectStore(this.storeName);
    const submissionKey = `SUBMISSION_${candidateAssessmentId}`;

    // ✅ FIX: Wrap IDBRequest in promise
    const record = await this.wrapIDBRequest(store.get(submissionKey));

    if (!record || !record.isFinalSubmission) return null;

    return {
      candidateAssessmentId: record.candidateAssessmentId,
      payload: record.submissionPayload,
      assessmentId: record.assessmentId,
      status: record.status,
      attempts: record.attempts,
      timestamp: record.timestamp,
      error: record.error,
    };
  }

  // ✅ NEW: Process pending final submission
  async processPendingSubmission(submitCandidateResultFn) {
    await this.init();

    const transaction = this.db.transaction([this.storeName], "readonly");
    const store = transaction.objectStore(this.storeName);

    // ✅ FIX: Wrap IDBRequest in promise
    const allRecords = await new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });

    const pendingSubmissions = allRecords.filter(
      (r) =>
        r.isFinalSubmission && (r.status === "pending" || r.status === "failed")
    );

    for (const submission of pendingSubmissions) {
      try {
        await this.updateUploadStatus(submission.questionId, "uploading");

        await submitCandidateResultFn(
          submission.assessmentId,
          submission.submissionPayload
        );

        await this.markUploadCompleted(submission.questionId);
      } catch (error) {
        if (submission.attempts >= submission.maxAttempts) {
          await this.updateUploadStatus(
            submission.questionId,
            "failed",
            error.message
          );
        } else {
          await this.updateUploadStatus(
            submission.questionId,
            "pending",
            error.message
          );
        }
      }
    }

    return pendingSubmissions.length;
  }

  // ✅ NEW: Get count of pending final submissions (without processing)
  async getPendingSubmissionsCount(candidateAssessmentId = null) {
    await this.init();

    const transaction = this.db.transaction([this.storeName], "readonly");
    const store = transaction.objectStore(this.storeName);

    // ✅ FIX: Wrap IDBRequest in promise
    const allRecords = await new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });

    const pendingSubmissions = allRecords.filter(
      (r) =>
        r.isFinalSubmission && (r.status === "pending" || r.status === "failed")
    );

    // If candidateAssessmentId is provided, filter by it
    if (candidateAssessmentId) {
      return pendingSubmissions.filter(
        (s) => s.candidateAssessmentId === candidateAssessmentId
      ).length;
    }

    return pendingSubmissions.length;
  }

  // ✅ NEW: Store pending draft for programming questions
  async storePendingDraft(questionId, draftData) {
    if (!offlineModeManager.isOfflineModeSupported()) {
      throw new Error("IndexedDB unavailable - cannot store offline data");
    }

    await this.init();

    const transaction = this.db.transaction([this.storeName], "readwrite");
    const store = transaction.objectStore(this.storeName);

    // ✅ Use composite key to avoid conflicts
    const compositeKey = `DRAFT_${questionId}_${draftData.languageId}`;

    const draftRecord = {
      questionId: compositeKey,
      originalQuestionId: questionId,
      languageId: draftData.languageId,
      code: draftData.code,
      candidateAssessmentId: draftData.candidateAssessmentId,
      assessmentId: draftData.assessmentId,
      skill: draftData.skill,
      // Store question metadata
      questionTitle: draftData.questionTitle || null,
      questionType: draftData.questionType || "programming",
      // Status tracking
      status: "pending",
      attempts: 0,
      maxAttempts: 3,
      timestamp: Date.now(),
      lastAttemptTime: null,
      error: null,
      // Flag to distinguish from other data
      isDraft: true,
    };

    return new Promise((resolve, reject) => {
      const request = store.put(draftRecord);
      request.onsuccess = () => {
        console.log(
          `✅ [IndexedDB Manager] Stored draft for question ${questionId}, language ${draftData.languageId}`
        );
        resolve(request.result);
      };
      request.onerror = () => {
        console.error(
          `❌ [IndexedDB Manager] Error storing draft for question ${questionId}:`,
          request.error
        );
        reject(request.error);
      };
    });
  }

  // ✅ NEW: Get pending draft
  async getPendingDraft(questionId, languageId) {
    await this.init();

    const transaction = this.db.transaction([this.storeName], "readonly");
    const store = transaction.objectStore(this.storeName);
    const compositeKey = `DRAFT_${questionId}_${languageId}`;

    const record = await this.wrapIDBRequest(store.get(compositeKey));

    if (!record || !record.isDraft) return null;

    return {
      questionId: record.originalQuestionId,
      languageId: record.languageId,
      code: record.code,
      candidateAssessmentId: record.candidateAssessmentId,
      assessmentId: record.assessmentId,
      skill: record.skill,
      status: record.status,
      attempts: record.attempts || 0,
      maxAttempts: record.maxAttempts || 3,
      timestamp: record.timestamp,
      lastAttemptTime: record.lastAttemptTime,
      error: record.error,
    };
  }

  // ✅ NEW: Store pending editor events for programming questions
  async storePendingEditorEvents(questionId, eventsData) {
    if (!offlineModeManager.isOfflineModeSupported()) {
      throw new Error("IndexedDB unavailable - cannot store offline data");
    }

    await this.init();

    const transaction = this.db.transaction([this.storeName], "readwrite");
    const store = transaction.objectStore(this.storeName);

    // ✅ Use composite key to avoid conflicts
    const compositeKey = `EVENTS_${questionId}_${eventsData.languageId}`;

    const eventsRecord = {
      questionId: compositeKey,
      originalQuestionId: questionId,
      languageId: eventsData.languageId,
      events: eventsData.events, // Array of editor events
      candidateAssessmentId: eventsData.candidateAssessmentId,
      assessmentId: eventsData.assessmentId,
      skill: eventsData.skill,
      sessionStartTime: eventsData.sessionStartTime,
      totalEvents: eventsData.events?.length || 0,
      initialContext: eventsData.initialContext || null,
      // Store question metadata
      questionTitle: eventsData.questionTitle || null,
      questionType: eventsData.questionType || "programming",
      // Status tracking
      status: "pending",
      attempts: 0,
      maxAttempts: 3,
      timestamp: Date.now(),
      lastAttemptTime: null,
      error: null,
      // Flag to distinguish from other data
      isEditorEvents: true,
    };

    return new Promise((resolve, reject) => {
      const request = store.put(eventsRecord);
      request.onsuccess = () => {
        console.log(
          `✅ [IndexedDB Manager] Stored editor events for question ${questionId}, language ${
            eventsData.languageId
          } (${eventsData.events?.length || 0} events)`
        );
        resolve(request.result);
      };
      request.onerror = () => {
        console.error(
          `❌ [IndexedDB Manager] Error storing editor events for question ${questionId}:`,
          request.error
        );
        reject(request.error);
      };
    });
  }

  // ✅ NEW: Get pending editor events
  async getPendingEditorEvents(questionId, languageId) {
    await this.init();

    const transaction = this.db.transaction([this.storeName], "readonly");
    const store = transaction.objectStore(this.storeName);
    const compositeKey = `EVENTS_${questionId}_${languageId}`;

    const record = await this.wrapIDBRequest(store.get(compositeKey));

    if (!record || !record.isEditorEvents) return null;

    return {
      questionId: record.originalQuestionId,
      languageId: record.languageId,
      events: record.events,
      candidateAssessmentId: record.candidateAssessmentId,
      assessmentId: record.assessmentId,
      skill: record.skill,
      sessionStartTime: record.sessionStartTime,
      totalEvents: record.totalEvents,
      initialContext: record.initialContext,
      status: record.status,
      attempts: record.attempts || 0,
      maxAttempts: record.maxAttempts || 3,
      timestamp: record.timestamp,
      lastAttemptTime: record.lastAttemptTime,
      error: record.error,
    };
  }

  // ✅ NEW: Mark draft as completed
  async markDraftCompleted(questionId, languageId) {
    await this.init();
    const compositeKey = `DRAFT_${questionId}_${languageId}`;
    const transaction = this.db.transaction([this.storeName], "readwrite");
    const store = transaction.objectStore(this.storeName);

    // ✅ FIX: Wrap IDBRequest in promise to ensure deletion completes
    return new Promise((resolve, reject) => {
      const request = store.delete(compositeKey);

      request.onsuccess = () => {
        console.log(
          `✅ [IndexedDB Manager] Delete request succeeded for draft: ${compositeKey}`
        );
        // Wait for transaction to complete before resolving
      };

      request.onerror = () => {
        const error = request.error || new Error("Delete operation failed");
        console.error(
          `❌ [IndexedDB Manager] Delete request error for draft ${compositeKey}:`,
          error
        );
        reject(error);
      };

      // ✅ CRITICAL: Wait for transaction completion to ensure deletion is persisted
      transaction.oncomplete = () => {
        resolve();
      };

      transaction.onerror = () => {
        const error = transaction.error || new Error("Transaction failed");
        console.error(
          `❌ [IndexedDB Manager] Transaction error for draft ${compositeKey}:`,
          error
        );
        reject(error);
      };
    });
  }

  // ✅ NEW: Mark editor events as completed
  async markEditorEventsCompleted(questionId, languageId) {
    await this.init();
    const compositeKey = `EVENTS_${questionId}_${languageId}`;
    const transaction = this.db.transaction([this.storeName], "readwrite");
    const store = transaction.objectStore(this.storeName);

    // ✅ FIX: Wrap IDBRequest in promise to ensure deletion completes
    return new Promise((resolve, reject) => {
      const request = store.delete(compositeKey);

      request.onsuccess = () => {
        // Wait for transaction to complete before resolving
      };

      request.onerror = () => {
        const error = request.error || new Error("Delete operation failed");
        console.error(
          `❌ [IndexedDB Manager] Delete request error for editor events ${compositeKey}:`,
          error
        );
        reject(error);
      };

      // ✅ CRITICAL: Wait for transaction completion to ensure deletion is persisted
      transaction.oncomplete = () => {
        resolve();
      };

      transaction.onerror = () => {
        const error = transaction.error || new Error("Transaction failed");
        console.error(
          `❌ [IndexedDB Manager] Transaction error for editor events ${compositeKey}:`,
          error
        );
        reject(error);
      };
    });
  }

  // ✅ NEW: Get all pending items with metadata for processing page
  async getAllPendingItemsWithMetadata(questionsArray = []) {
    await this.init();

    const transaction = this.db.transaction([this.storeName], "readonly");
    const store = transaction.objectStore(this.storeName);

    // Get all records
    const allRecords = await new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });

    // Filter pending items (exclude completed)
    const pendingRecords = allRecords.filter(
      (r) =>
        (r.status === "pending" ||
          r.status === "failed" ||
          r.status === "uploading" ||
          r.status === "retrying") &&
        !r.isFinalSubmission
    );

    // Group by questionId and enrich with metadata
    const questionMap = new Map();

    for (const record of pendingRecords) {
      const questionId = record.originalQuestionId || record.questionId;

      // Skip if already processed and completed
      if (questionMap.has(questionId)) {
        const existing = questionMap.get(questionId);
        // Keep the one with the earliest stage (draft < events < answer < upload)
        const currentPriority = this.getRecordPriority(record);
        const existingPriority = this.getRecordPriority(existing.record);
        if (currentPriority < existingPriority) {
          questionMap.set(questionId, {
            questionId,
            record,
            priority: currentPriority,
          });
        }
        continue;
      }

      // Get question metadata
      let questionTitle = record.questionTitle || null;
      let questionType = record.questionType || null;

      // Fallback to questions array from Redux
      if (!questionTitle || !questionType) {
        const question = questionsArray.find((q) => q._id === questionId);
        if (question) {
          questionTitle = questionTitle || question.questionTitle || null;
          questionType = questionType || question.type || null;
        }
      }

      // Last resort: use questionId
      if (!questionTitle) {
        questionTitle = questionId;
      }
      if (!questionType) {
        // Infer from record type
        if (record.isDraft || record.isEditorEvents) {
          questionType = "programming";
        } else if (record.isTextAnswer) {
          questionType = record.questionType || "unknown";
        } else {
          questionType = record.fileType?.includes("video") ? "video" : "audio";
        }
      }

      questionMap.set(questionId, {
        questionId,
        questionTitle,
        questionType,
        record,
        priority: this.getRecordPriority(record),
      });
    }

    // Convert to array and sort by priority
    return Array.from(questionMap.values())
      .map((item) => ({
        questionId: item.questionId,
        questionTitle: item.questionTitle,
        questionType: item.questionType,
        status: item.record.status,
        attempts: item.record.attempts || 0,
        maxAttempts: item.record.maxAttempts || 3,
        timestamp: item.record.timestamp,
        error: item.record.error,
        isDraft: item.record.isDraft || false,
        isEditorEvents: item.record.isEditorEvents || false,
        isTextAnswer: item.record.isTextAnswer || false,
        isFileUpload:
          !item.record.isTextAnswer &&
          !item.record.isDraft &&
          !item.record.isEditorEvents,
      }))
      .sort((a, b) => {
        // Sort by timestamp (oldest first)
        return (a.timestamp || 0) - (b.timestamp || 0);
      });
  }

  // Helper to determine record priority (lower = earlier stage)
  getRecordPriority(record) {
    if (record.isDraft) return 1;
    if (record.isEditorEvents) return 2;
    if (record.isTextAnswer) return 3;
    if (record.isFileUpload) return 4;
    return 5;
  }

  // ✅ NEW: Get question processing status with current stage
  async getQuestionProcessingStatus(questionId, questionType = null) {
    await this.init();

    // Get active retries from managers
    const { uploadRetryManager } = await import("./uploadRetryManager");
    const { textAnswerRetryManager } = await import("./textAnswerRetryManager");

    const isUploadRetrying = uploadRetryManager.isRetrying(questionId);
    const isAnswerRetrying = textAnswerRetryManager.isRetrying(questionId);

    // Check for pending items
    const transaction = this.db.transaction([this.storeName], "readonly");
    const store = transaction.objectStore(this.storeName);

    // Get all records for this question
    const allRecords = await new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });

    const questionRecords = allRecords.filter((r) => {
      const recordQuestionId = r.originalQuestionId || r.questionId;
      return (
        recordQuestionId === questionId ||
        recordQuestionId === `ANSWER_${questionId}`
      );
    });

    // Check for pending draft (programming only)
    const pendingDraft = questionRecords.find(
      (r) => r.isDraft && (r.status === "pending" || r.status === "failed")
    );

    // Check for pending editor events (programming only)
    const pendingEvents = questionRecords.find(
      (r) =>
        r.isEditorEvents && (r.status === "pending" || r.status === "failed")
    );

    // Check for pending file upload
    const pendingUpload = questionRecords.find(
      (r) =>
        !r.isTextAnswer &&
        !r.isDraft &&
        !r.isEditorEvents &&
        !r.isFinalSubmission &&
        (r.status === "pending" ||
          r.status === "failed" ||
          r.status === "uploading")
    );

    // Check for pending answer
    const pendingAnswer = questionRecords.find(
      (r) =>
        r.isTextAnswer &&
        r.originalQuestionId === questionId &&
        (r.status === "pending" || r.status === "failed")
    );

    // Determine current stage based on question type
    let currentStage = "completed";
    let status = "completed";
    let progress = 100;
    let error = null;

    // Infer question type if not provided
    if (!questionType) {
      if (pendingDraft || pendingEvents) {
        questionType = "programming";
      } else if (pendingUpload) {
        questionType = pendingUpload.fileType?.includes("video")
          ? "video"
          : "audio";
      } else if (pendingAnswer) {
        questionType = pendingAnswer.questionType || "unknown";
      }
    }

    // Determine stage based on what's pending
    if (pendingDraft && pendingDraft.status !== "completed") {
      currentStage = "draft-saving";
      status =
        pendingDraft.status === "failed"
          ? "failed"
          : isUploadRetrying
            ? "processing"
            : "pending";
      progress = 25;
      error = pendingDraft.error;
    } else if (pendingEvents && pendingEvents.status !== "completed") {
      currentStage = "events-uploading";
      status =
        pendingEvents.status === "failed"
          ? "failed"
          : isUploadRetrying
            ? "processing"
            : "pending";
      progress = 50;
      error = pendingEvents.error;
    } else if (pendingUpload && pendingUpload.status !== "completed") {
      if (questionType === "video" || questionType === "audio") {
        currentStage =
          pendingUpload.status === "uploading" ? "uploading" : "processing";
        status =
          pendingUpload.status === "failed"
            ? "failed"
            : isUploadRetrying
              ? "processing"
              : "pending";
        progress = pendingUpload.status === "uploading" ? 66 : 33;
        error = pendingUpload.error;
      }
    } else if (pendingAnswer && pendingAnswer.status !== "completed") {
      currentStage = "submitting";
      status =
        pendingAnswer.status === "failed"
          ? "failed"
          : isAnswerRetrying
            ? "processing"
            : "pending";
      progress = questionType === "programming" ? 75 : 50;
      error = pendingAnswer.error;
    } else {
      // All complete
      currentStage = "completed";
      status = "completed";
      progress = 100;
    }

    return {
      currentStage,
      status,
      progress,
      error,
      attempts: pendingAnswer?.attempts || pendingUpload?.attempts || 0,
      maxAttempts:
        pendingAnswer?.maxAttempts || pendingUpload?.maxAttempts || 3,
    };
  }
}

export const indexedDBUploadManager = new IndexedDBUploadManager();

